import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Archive, Download, RotateCcw, Upload, AlertTriangle, CheckCircle2, Loader2, Mail, RefreshCw, ShieldAlert } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const CHUNK = 2 * 1024 * 1024;
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—");
const KIND_LABEL = { nightly: "Nightly", manual: "Manual", pre_restore: "Safety (pre-restore)" };
const STATUS_CLS = { completed: "bg-green-100 text-green-700", running: "bg-sky-100 text-sky-700", failed: "bg-red-100 text-red-700", queued: "bg-sky-100 text-sky-700", previewed: "bg-slate-100 text-slate-600" };
const ACTION_CLS = { replace: "text-amber-700", create: "text-green-700", drop: "text-red-600", empty: "text-slate-500", keep: "text-slate-400" };
const ACTION_LABEL = { replace: "Replace", create: "Create", drop: "Remove", empty: "Empty (preview)", keep: "Keep current" };

const StatusPill = ({ status }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[status] || "bg-slate-100"}`}>{status}</span>
);

function EnvHeader({ info, onToggleEmail }) {
  const prod = info.env === "production";
  return (
    <div className={`rounded-2xl p-4 border ${prod ? "bg-red-50/60 border-red-200" : "bg-emerald-50/60 border-emerald-200"}`} data-testid="backups-env-header">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Archive className={`w-5 h-5 mt-0.5 ${prod ? "text-red-600" : "text-emerald-700"}`} />
          <div>
            <p className="font-bold text-[#1e3a5f]">Database backups <span className={`ml-1 text-[10px] uppercase px-2 py-0.5 rounded-full ${prod ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`} data-testid="backups-env-badge">{info.env}</span></p>
            <p className="text-xs text-slate-600 mt-0.5">
              {prod
                ? "Nightly backup at 02:00 (Berlin). Kept 30 days + the first backup of each month for 12 months. Restore here is for disaster recovery only."
                : "Restore a production backup here to sync preview. Stripe IDs are cleared, preview admins and keys are kept, and outgoing email is switched off."}
            </p>
          </div>
        </div>
        {!prod && (
          <button onClick={onToggleEmail} data-testid="preview-email-toggle"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${info.emails_enabled ? "bg-white text-amber-700 border-amber-300" : "bg-white text-slate-500"}`}>
            <Mail className="w-3.5 h-3.5" /> Outgoing email: {info.emails_enabled ? "ON" : "OFF"}
          </button>
        )}
      </div>
      {info.last_restore && <LastRestoreBanner job={info.last_restore} />}
    </div>
  );
}

function LastRestoreBanner({ job }) {
  const failed = job.status === "failed";
  return (
    <div className={`mt-3 text-xs rounded-xl px-3 py-2 ${failed ? "bg-red-100 text-red-800" : "bg-white/70 text-slate-700"}`} data-testid="last-restore-banner">
      <b>Last restore:</b> {job.status} · by {job.applied_by || job.created_by} · {fmtTime(job.finished_at || job.started_at || job.created_at)}
      {" "}· source {job.source?.env} backup of {fmtTime(job.source?.created_at)}
      {job.status === "running" && ` · ${job.progress?.length || 0} collection(s) done`}
      {failed && job.error && <div className="mt-1 font-mono">{job.error}</div>}
    </div>
  );
}

function BackupRow({ b, onDownload, onRestore, busy }) {
  return (
    <tr className="border-t" data-testid={`backup-row-${b.id}`}>
      <td className="py-2 pr-2 whitespace-nowrap">{b.date}<div className="text-[10px] text-slate-400">{fmtTime(b.created_at)}</div></td>
      <td className="py-2 pr-2">{KIND_LABEL[b.kind] || b.kind}<div className="text-[10px] text-slate-400">{b.triggered_by}</div></td>
      <td className="py-2 pr-2"><StatusPill status={b.status} />{b.error && <div className="text-[10px] text-red-500 max-w-[200px] truncate" title={b.error}>{b.error}</div>}</td>
      <td className="py-2 pr-2 text-right">{b.size ? fmtSize(b.size) : "—"}</td>
      <td className="py-2 pr-2 text-right">{b.total_docs ?? "—"}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {b.status === "completed" && (
          <>
            <button onClick={() => onDownload(b)} disabled={busy} className="p-1.5 rounded-full hover:bg-slate-100 text-[#1e3a5f]" title="Download ZIP" data-testid={`backup-download-${b.id}`}><Download className="w-4 h-4" /></button>
            <button onClick={() => onRestore(b)} disabled={busy} className="p-1.5 rounded-full hover:bg-amber-50 text-amber-600" title="Restore from this backup" data-testid={`backup-restore-${b.id}`}><RotateCcw className="w-4 h-4" /></button>
          </>
        )}
      </td>
    </tr>
  );
}

function BackupTable({ backups, onDownload, onRestore, busy }) {
  if (!backups.length) return <p className="text-sm text-slate-400 text-center py-8" data-testid="backups-empty">No backups yet</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="backups-table">
        <thead><tr className="text-left text-[11px] uppercase text-slate-400">
          <th className="pb-2">Date</th><th className="pb-2">Type</th><th className="pb-2">Status</th>
          <th className="pb-2 text-right">Size</th><th className="pb-2 text-right">Records</th><th className="pb-2" />
        </tr></thead>
        <tbody>{backups.map(b => <BackupRow key={b.id} b={b} onDownload={onDownload} onRestore={onRestore} busy={busy} />)}</tbody>
      </table>
    </div>
  );
}

function UploadRestore({ onJob }) {
  const { authAxios } = useAuth();
  const [pct, setPct] = useState(null);
  const inputRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) return toast.error("Choose a backup .zip file");
    const total = Math.ceil(file.size / CHUNK);
    try {
      setPct(0);
      const { data: init } = await authAxios.post("/platform/db-backups/restore/upload-init", { filename: file.name, size: file.size });
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await authAxios.post("/platform/db-backups/restore/upload-chunk", fd);
        setPct(Math.round(((i + 1) / total) * 90));
      }
      const { data: job } = await authAxios.post("/platform/db-backups/restore/upload-complete", { upload_id: init.upload_id, total_chunks: total, filename: file.name });
      setPct(100);
      onJob(job);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setPct(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <label className={`flex items-center gap-2 px-4 py-2 rounded-full border border-dashed text-sm cursor-pointer ${pct !== null ? "bg-slate-100 text-slate-400" : "bg-white text-[#1e3a5f] hover:bg-slate-50"}`} data-testid="restore-upload-label">
      {pct !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      {pct !== null ? `Uploading & checking… ${pct}%` : "Upload backup ZIP to restore"}
      <input ref={inputRef} type="file" accept=".zip,application/zip" className="hidden" disabled={pct !== null} onChange={e => upload(e.target.files?.[0])} data-testid="restore-upload-input" />
    </label>
  );
}

function PlanTable({ rows }) {
  return (
    <div className="max-h-[38vh] overflow-y-auto border rounded-xl">
      <table className="w-full text-xs" data-testid="restore-plan-table">
        <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-slate-500">
          <th className="p-2">Collection</th><th className="p-2">Action</th><th className="p-2 text-right">Now</th><th className="p-2 text-right">Backup</th><th className="p-2 text-right">+ New</th><th className="p-2 text-right">~ Changed</th><th className="p-2 text-right">− Removed</th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.name} className="border-t" data-testid={`restore-plan-row-${r.name}`}>
              <td className="p-2 font-mono">{r.name}</td>
              <td className={`p-2 font-medium ${ACTION_CLS[r.action]}`}>{ACTION_LABEL[r.action]}</td>
              <td className="p-2 text-right">{r.current}</td>
              <td className="p-2 text-right">{r.action === "keep" ? "—" : r.backup}</td>
              <td className="p-2 text-right text-green-700">{r.added ?? ""}</td>
              <td className="p-2 text-right text-amber-700">{r.changed ?? ""}</td>
              <td className="p-2 text-right text-red-600">{r.action === "drop" || r.action === "empty" ? r.current : (r.removed ?? "")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RestoreDialog({ job, env, phrase, onClose, onApplied }) {
  const { authAxios } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const prod = env === "production";
  const integrityErrors = job.plan?.integrity_errors || [];
  const ready = confirm.trim() === phrase && (!prod || password) && !integrityErrors.length && !busy;
  const apply = async () => {
    setBusy(true);
    try {
      await authAxios.post(`/platform/db-backups/restore/${job.id}/apply`, { confirm: confirm.trim(), password });
      toast.success("Restore started — a safety backup is taken first. Sys admins are alerted by email.");
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed to start");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="restore-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#1e3a5f]"><ShieldAlert className={`w-5 h-5 ${prod ? "text-red-600" : "text-amber-500"}`} /> Preview of the restore</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 space-y-1" data-testid="restore-source">
          <p>Backup from <b>{job.source?.env}</b> · {fmtTime(job.source?.created_at)} · {job.source?.kind} · {job.source?.total_docs} records</p>
          <p>Target: <b>{env}</b> database. Nothing has been changed yet.</p>
        </div>
        {integrityErrors.length > 0 && (
          <p className="text-xs bg-red-100 text-red-800 rounded-lg px-3 py-2" data-testid="restore-integrity-error">Integrity check failed for: {integrityErrors.join(", ")} — this backup cannot be restored.</p>
        )}
        {prod && (
          <p className="text-xs bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> Disaster recovery only. Every change made in production since this backup will be lost (a safety backup of the current state is taken first).</p>
        )}
        <PlanTable rows={job.plan?.rows || []} />
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-slate-500">Type <b className="font-mono">{phrase}</b> to confirm</label>
            <input value={confirm} onChange={e => setConfirm(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm font-mono" data-testid="restore-confirm-input" />
          </div>
          {prod && (
            <div>
              <label className="text-xs text-slate-500">Your sys admin password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="restore-password-input" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-slate-600 hover:bg-slate-100" data-testid="restore-cancel-btn">Cancel</button>
          <button onClick={apply} disabled={!ready} className={`px-4 py-2 rounded-full text-sm font-bold text-white ${prod ? "bg-red-600" : "bg-amber-500"} disabled:opacity-40`} data-testid="restore-apply-btn">
            {busy ? "Starting…" : "Restore now"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DbBackups() {
  const { authAxios } = useAuth();
  const [info, setInfo] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => authAxios.get("/platform/db-backups").then(r => setInfo(r.data)).catch(e => toast.error(e.response?.data?.detail || "Failed to load backups")), [authAxios]);
  useEffect(() => { load(); }, []); // eslint-disable-line
  const active = info && (info.backups.some(b => b.status === "running") || ["running", "queued"].includes(info.last_restore?.status));
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [active, load]);

  const runNow = async () => {
    try { await authAxios.post("/platform/db-backups/run"); toast.success("Backup started"); setTimeout(load, 800); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start backup"); }
  };
  const download = async (b) => {
    setBusy(true);
    try {
      const r = await authAxios.get(`/platform/db-backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `mymaitree-${b.env}-${b.kind}-${b.date}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); } finally { setBusy(false); }
  };
  const restoreFrom = async (b) => {
    setBusy(true);
    try { const r = await authAxios.post(`/platform/db-backups/${b.id}/restore-plan`); setJob(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not prepare restore"); } finally { setBusy(false); }
  };
  const toggleEmail = async () => {
    try { await authAxios.post("/platform/db-backups/preview-email", { enabled: !info.emails_enabled }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!info) return <div className="text-center py-8 text-slate-400">Loading backups…</div>;
  return (
    <div className="space-y-4" data-testid="db-backups">
      <EnvHeader info={info} onToggleEmail={toggleEmail} />
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={runNow} disabled={active} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-medium disabled:opacity-50" data-testid="backup-run-now-btn">
          {active ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Create backup now
        </button>
        <UploadRestore onJob={setJob} />
        <button onClick={load} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" title="Reload" data-testid="backups-reload-btn"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <div className="bg-white rounded-2xl p-4 shadow-sm">
        <BackupTable backups={info.backups} onDownload={download} onRestore={restoreFrom} busy={busy} />
      </div>
      {job && <RestoreDialog job={job} env={info.env} phrase={info.confirm_phrase} onClose={() => setJob(null)} onApplied={() => { setJob(null); load(); }} />}
    </div>
  );
}
